import React, { useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";

const EMPTY = {
  title: "", location: "", system_size: "", category: "residential",
  savings: "", description: "", before_image: "", after_image: "",
  customer_feedback: "", customer_name: "", published: true,
};
const CATEGORIES = ["residential", "commercial", "industrial"];

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/projects").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await api.put(`/admin/projects/${editing.id}`, editing);
      else await api.post("/admin/projects", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="h2 text-[#0A1F15]">Projects</h1>
          <p className="text-[#5C7368]">{items.length} projects</p>
        </div>
        <button onClick={() => setEditing({...EMPTY})} className="btn-primary !py-2.5 text-sm">
          <Plus weight="bold" size={16}/> New Project
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(p => (
          <div key={p.id} className="bg-white rounded-2xl border border-[#E5E3DB] overflow-hidden">
            <div className="aspect-[16/9] overflow-hidden bg-[#F0EFEB]">
              {p.after_image && <img src={p.after_image} alt={p.title} className="w-full h-full object-cover"/>}
            </div>
            <div className="p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#135934] mb-1">{p.category}</div>
              <h3 className="font-bold mb-1 text-[#0A1F15]">{p.title}</h3>
              <div className="text-xs text-[#5C7368] mb-3">{p.location} · {p.system_size}</div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(p)} className="flex-1 py-1.5 rounded border border-[#E5E3DB] text-sm hover:bg-[#F9F8F6] flex items-center justify-center gap-1"><PencilSimple size={14}/> Edit</button>
                <button onClick={() => remove(p.id)} className="px-3 py-1.5 rounded border border-red-200 text-red-600 text-sm hover:bg-red-50"><Trash size={14}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E3DB] sticky top-0 bg-white">
              <h2 className="h3">{editing.id ? "Edit" : "New"} project</h2>
              <button type="button" onClick={() => setEditing(null)} className="p-2 rounded hover:bg-[#F0EFEB]"><X size={20}/></button>
            </div>
            <div className="p-5 space-y-4">
              <F label="Title"><input required value={editing.title} onChange={e => setEditing({...editing,title:e.target.value})} className="inp"/></F>
              <div className="grid md:grid-cols-2 gap-3">
                <F label="Location"><input required value={editing.location} onChange={e => setEditing({...editing,location:e.target.value})} className="inp"/></F>
                <F label="Category">
                  <select value={editing.category} onChange={e => setEditing({...editing,category:e.target.value})} className="inp">
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </F>
                <F label="System Size"><input required placeholder="5 kW" value={editing.system_size} onChange={e => setEditing({...editing,system_size:e.target.value})} className="inp"/></F>
                <F label="Savings"><input required placeholder="₹72,000 / year" value={editing.savings} onChange={e => setEditing({...editing,savings:e.target.value})} className="inp"/></F>
              </div>
              <F label="Description"><textarea rows={3} value={editing.description} onChange={e => setEditing({...editing,description:e.target.value})} className="inp resize-none"/></F>
              <div className="grid md:grid-cols-2 gap-3">
                <F label="Before Image URL"><input value={editing.before_image} onChange={e => setEditing({...editing,before_image:e.target.value})} className="inp"/></F>
                <F label="After Image URL"><input value={editing.after_image} onChange={e => setEditing({...editing,after_image:e.target.value})} className="inp"/></F>
              </div>
              <F label="Customer Feedback"><textarea rows={2} value={editing.customer_feedback || ""} onChange={e => setEditing({...editing,customer_feedback:e.target.value})} className="inp resize-none"/></F>
              <F label="Customer Name"><input value={editing.customer_name || ""} onChange={e => setEditing({...editing,customer_name:e.target.value})} className="inp"/></F>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.published} onChange={e => setEditing({...editing,published:e.target.checked})}/> Published</label>
            </div>
            <div className="p-5 border-t border-[#E5E3DB] flex justify-end gap-2 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setEditing(null)} className="btn-outline-dark !py-2.5 text-sm">Cancel</button>
              <button className="btn-primary !py-2.5 text-sm">Save</button>
            </div>
          </form>
        </div>
      )}

      <style>{`.inp{width:100%;padding:0.75rem 1rem;border-radius:0.75rem;border:1px solid #E5E3DB;background:white;}`}</style>
    </div>
  );
}
function F({label,children}){return(<div><label className="block text-xs font-bold uppercase tracking-wider text-[#5C7368] mb-1.5">{label}</label>{children}</div>);}
